import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { apiClient } from "../../api/apiServices";
import ServiceGate from "../../components/feature/ServiceGate";
import { useServices } from "../../hooks/useServices";

const NIGERIAN_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","FCT","Gombe","Imo",
  "Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara","Lagos","Nasarawa",
  "Niger","Ogun","Ondo","Osun","Oyo","Plateau","Rivers","Sokoto","Taraba",
  "Yobe","Zamfara",
];

const ID_TYPES = [
  { value: "nin",                    label: "NIN Slip",               hasFront: true,  hasBack: false },
  { value: "drivers_license",        label: "Driver's License",       hasFront: true,  hasBack: true  },
  { value: "voters_card",            label: "Voter's Card",           hasFront: true,  hasBack: true  },
  { value: "international_passport", label: "International Passport", hasFront: true,  hasBack: false },
];

const STEPS = [
  { id: 1, title: "Personal Info",  icon: "ri-user-line" },
  { id: 2, title: "Identity",       icon: "ri-id-card-line" },
  { id: 3, title: "Documents",      icon: "ri-file-image-line" },
  { id: 4, title: "Review",         icon: "ri-checkbox-circle-line" },
];

// ── Upload box — handles image preview + PDF filename display ──
function UploadBox({ label, sublabel, name, file, onChange, required = true }) {
  const ref = useRef();
  const isPdf    = file?.type === "application/pdf";
  const isImage  = file && !isPdf;
  const preview  = isImage ? URL.createObjectURL(file) : null;
  const fileSizeKB = file ? Math.round(file.size / 1024) : 0;

  return (
    <div>
      <p className="text-secondary text-sm font-semibold mb-1.5">
        {label} {required && <span className="text-danger">*</span>}
      </p>
      {sublabel && <p className="text-muted text-xs mb-2">{sublabel}</p>}

      <div
        onClick={() => ref.current.click()}
        className={`relative border-2 border-dashed rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden ${
          file
            ? "border-brand-border bg-brand-light"
            : "border-border-strong bg-surface-muted hover:border-brand-border hover:bg-brand-light"
        }`}
        style={{ minHeight: 120 }}
      >
        {/* ── Image preview ── */}
        {isImage && (
          <>
            <img src={preview} alt={label} className="w-full h-32 object-cover rounded-2xl" />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity rounded-2xl">
              <p className="text-white text-xs font-semibold">Tap to change</p>
            </div>
          </>
        )}

        {/* ── PDF preview ── */}
        {isPdf && (
          <div className="py-5 px-4 flex flex-col items-center gap-2 w-full">
            <div className="w-12 h-12 bg-danger-light rounded-xl flex items-center justify-center">
              <i className="ri-file-pdf-2-line text-danger text-2xl" />
            </div>
            <p className="text-primary text-xs font-semibold text-center truncate max-w-full px-2">
              {file.name}
            </p>
            <p className="text-muted text-[10px]">
              {fileSizeKB < 1024 ? `${fileSizeKB} KB` : `${(fileSizeKB / 1024).toFixed(1)} MB`}
            </p>
            <span className="text-[10px] text-brand-text bg-brand-light px-2.5 py-0.5 rounded-full font-semibold">
              ✓ PDF selected — tap to change
            </span>
          </div>
        )}

        {/* ── Empty state ── */}
        {!file && (
          <div className="py-6 flex flex-col items-center gap-2">
            <div className="w-10 h-10 bg-surface-hover rounded-xl flex items-center justify-center">
              <i className="ri-upload-cloud-2-line text-muted text-xl" />
            </div>
            <p className="text-secondary text-xs font-medium">Tap to upload</p>
            <p className="text-faint text-[10px]">JPG, PNG or PDF • Max 5MB</p>
          </div>
        )}
      </div>

      <input ref={ref} type="file" name={name} accept="image/*,.pdf" className="hidden"
        onChange={e => onChange(e.target.files[0])} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export default function KYCPage() {
  const { services } = useServices();
  const navigate = useNavigate();
  const [step, setStep]           = useState(1);
  const [kycStatus, setKycStatus] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [personal, setPersonal] = useState({
    firstName: "", lastName: "", middleName: "",
    dateOfBirth: "", gender: "", phoneNumber: "",
    address: "", state: "", lga: "",
  });

  const [identity, setIdentity] = useState({
    idType: "", idNumber: "", idExpiry: "", nin: "",
  });

  const [docs, setDocs] = useState({
    frontImage: null, backImage: null, selfieImage: null,
  });

  const selectedIdType = ID_TYPES.find(t => t.value === identity.idType);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await apiClient.get("/mtrade/kyc/status");
        if (res.data.status) {
          const data = res.data.data;
          setKycStatus(data);
          if (data.personalInfo) setPersonal(prev => ({ ...prev, ...data.personalInfo }));
          if (data.idDocument)   setIdentity(prev => ({ ...prev, idType: data.idDocument.type || "", idNumber: data.idDocument.number || "", idExpiry: data.idDocument.expiryDate || "" }));
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchStatus();
  }, []);

  const updateP = (k, v) => setPersonal(prev => ({ ...prev, [k]: v }));
  const updateI = (k, v) => setIdentity(prev => ({ ...prev, [k]: v }));

  const step1Valid = personal.firstName && personal.lastName && personal.dateOfBirth && personal.phoneNumber && personal.address && personal.state;
  const step2Valid = identity.idType && identity.idNumber;
  const step3Valid = docs.frontImage && docs.selfieImage && (!selectedIdType?.hasBack || docs.backImage);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(personal).forEach(([k, v]) => formData.append(k, v));
      formData.append("idType",   identity.idType);
      formData.append("idNumber", identity.idNumber);
      formData.append("idExpiry", identity.idExpiry);
      if (identity.nin)     formData.append("nin",        identity.nin);
      if (docs.frontImage)  formData.append("frontImage",  docs.frontImage);
      if (docs.backImage)   formData.append("backImage",   docs.backImage);
      if (docs.selfieImage) formData.append("selfieImage", docs.selfieImage);

      const res = await apiClient.post("/mtrade/kyc/submit", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data.status) {
        setKycStatus({ status: "submitted" });
        toast.success("KYC submitted! Review takes 24–48 hours.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Status screens ─────────────────────────────────────────
  if (loading) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-t-brand rounded-full animate-spin" style={{ borderColor: "var(--color-brand-light)", borderTopColor: "var(--color-brand)" }} />
    </div>
  );

  if (kycStatus?.status === "approved") return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 text-center">
      <div className="w-20 h-20 bg-brand-light rounded-3xl flex items-center justify-center mb-5">
        <i className="ri-shield-check-fill text-brand text-4xl" />
      </div>
      <h2 className="text-primary font-bold text-xl mb-2 font-display">Identity Verified</h2>
      <p className="text-muted text-sm mb-6">Your KYC has been approved. You have full access.</p>
      <button onClick={() => navigate("/home")} className="px-8 py-3 bg-brand text-on-brand font-bold rounded-2xl text-sm">Back to Home</button>
    </div>
  );

  if (kycStatus?.status === "submitted" || kycStatus?.status === "under_review") return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 text-center">
      <div className="w-20 h-20 bg-warning-light rounded-3xl flex items-center justify-center mb-5">
        <i className="ri-time-line text-warning text-4xl" />
      </div>
      <h2 className="text-primary font-bold text-xl mb-2 font-display">Under Review</h2>
      <p className="text-muted text-sm mb-2">Your documents have been submitted and are being reviewed.</p>
      <p className="text-muted text-xs mb-6">This usually takes 24–48 hours. We'll notify you.</p>
      <button onClick={() => navigate("/home")} className="px-8 py-3 bg-surface border border-border text-secondary font-bold rounded-2xl text-sm">Back to Home</button>
    </div>
  );

  if (kycStatus?.status === "rejected") return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 text-center">
      <div className="w-20 h-20 bg-danger-light rounded-3xl flex items-center justify-center mb-5">
        <i className="ri-close-circle-fill text-danger text-4xl" />
      </div>
      <h2 className="text-primary font-bold text-xl mb-2 font-display">KYC Not Approved</h2>
      <div className="bg-danger-light border border-danger-border rounded-2xl px-5 py-4 mb-6 text-left max-w-sm">
        <p className="text-danger-text text-xs font-semibold mb-1">Reason:</p>
        <p className="text-danger-text text-sm">{kycStatus.rejectionReason}</p>
      </div>
      <button onClick={() => setKycStatus({ status: "not_started" })} className="px-8 py-3 bg-brand text-on-brand font-bold rounded-2xl text-sm">Resubmit KYC</button>
    </div>
  );

  if (kycStatus?.status === "info_requested") return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-5 text-center">
      <div className="w-20 h-20 bg-info-light rounded-3xl flex items-center justify-center mb-5">
        <i className="ri-information-line text-info text-4xl" />
      </div>
      <h2 className="text-primary font-bold text-xl mb-2 font-display">More Information Needed</h2>
      <div className="bg-info-light border border-info-border rounded-2xl px-5 py-4 mb-6 text-left max-w-sm">
        <p className="text-info-text text-xs font-semibold mb-1">Our team says:</p>
        <p className="text-info-text text-sm">{kycStatus.infoRequested}</p>
      </div>
      <button onClick={() => setKycStatus({ status: "not_started" })} className="px-8 py-3 bg-brand text-on-brand font-bold rounded-2xl text-sm">Update & Resubmit</button>
    </div>
  );

  // ── Main form ──────────────────────────────────────────────
  return (
    <ServiceGate serviceKey="kyc" services={services} fullPage>
      <div className="min-h-screen bg-bg pb-10 font-display">
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center gap-3 mb-5">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-lg">Verify Identity</h1>
            <p className="text-muted text-xs">Complete KYC to unlock all features</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {STEPS.map((s, i) => (
            <div key={s.id} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all flex-shrink-0 ${
                step > s.id ? "bg-brand text-on-brand" : step === s.id ? "bg-brand text-on-brand" : "bg-surface-hover text-faint"
              }`} style={step === s.id ? { boxShadow: "0 0 0 4px var(--color-brand-ring)" } : {}}>
                {step > s.id ? <i className="ri-check-line text-xs" /> : s.id}
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-1 rounded-full ${step > s.id ? "bg-brand-border" : "bg-border"}`} />}
            </div>
          ))}
        </div>
        <p className="text-secondary text-xs mt-2 font-medium">Step {step} of {STEPS.length}: {STEPS[step - 1].title}</p>
      </div>

      <div className="px-5 pt-5">
        {/* STEP 1 */}
        {step === 1 && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[{ label: "First Name", key: "firstName", placeholder: "As on ID" }, { label: "Last Name", key: "lastName", placeholder: "As on ID" }].map(f => (
                <div key={f.key}>
                  <label className="text-secondary text-xs font-semibold block mb-1.5">{f.label} <span className="text-danger">*</span></label>
                  <input value={personal[f.key]} onChange={e => updateP(f.key, e.target.value)} placeholder={f.placeholder} className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-secondary text-xs font-semibold block mb-1.5">Middle Name</label>
              <input value={personal.middleName} onChange={e => updateP("middleName", e.target.value)} placeholder="Optional" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-secondary text-xs font-semibold block mb-1.5">Date of Birth <span className="text-danger">*</span></label>
                <input type="date" value={personal.dateOfBirth} onChange={e => updateP("dateOfBirth", e.target.value)} className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary focus:outline-none focus:border-brand" />
              </div>
              <div>
                <label className="text-secondary text-xs font-semibold block mb-1.5">Gender</label>
                <select value={personal.gender} onChange={e => updateP("gender", e.target.value)} className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary focus:outline-none focus:border-brand">
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-secondary text-xs font-semibold block mb-1.5">Phone Number <span className="text-danger">*</span></label>
              <input value={personal.phoneNumber} onChange={e => updateP("phoneNumber", e.target.value.replace(/\D/g, "").slice(0, 11))} placeholder="0801 234 5678" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand" />
            </div>
            <div>
              <label className="text-secondary text-xs font-semibold block mb-1.5">Residential Address <span className="text-danger">*</span></label>
              <input value={personal.address} onChange={e => updateP("address", e.target.value)} placeholder="House number, street, area" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-secondary text-xs font-semibold block mb-1.5">State <span className="text-danger">*</span></label>
                <select value={personal.state} onChange={e => updateP("state", e.target.value)} className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary focus:outline-none focus:border-brand">
                  <option value="">Select state</option>
                  {NIGERIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-secondary text-xs font-semibold block mb-1.5">LGA</label>
                <input value={personal.lga} onChange={e => updateP("lga", e.target.value)} placeholder="Local Govt Area" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-brand" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="space-y-5">
            <div>
              <p className="text-secondary text-sm font-semibold mb-3">ID Type <span className="text-danger">*</span></p>
              <div className="grid grid-cols-2 gap-2">
                {ID_TYPES.map(t => (
                  <button key={t.value} onClick={() => updateI("idType", t.value)} className={`p-3.5 rounded-2xl border-2 text-left transition-all ${identity.idType === t.value ? "border-brand bg-brand-light" : "border-border bg-surface hover:border-brand-border"}`}>
                    <p className={`text-sm font-semibold ${identity.idType === t.value ? "text-brand-text" : "text-secondary"}`}>{t.label}</p>
                    <p className="text-xs text-muted mt-0.5">{t.hasBack ? "Front & back" : "Front only"}</p>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-secondary text-xs font-semibold block mb-1.5">ID Number <span className="text-danger">*</span></label>
              <input value={identity.idNumber} onChange={e => updateI("idNumber", e.target.value)} placeholder="Enter your ID number" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary font-mono placeholder:text-faint focus:outline-none focus:border-brand" />
            </div>
            {(identity.idType === "international_passport" || identity.idType === "drivers_license") && (
              <div>
                <label className="text-secondary text-xs font-semibold block mb-1.5">Expiry Date</label>
                <input type="date" value={identity.idExpiry} onChange={e => updateI("idExpiry", e.target.value)} className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary focus:outline-none focus:border-brand" />
              </div>
            )}
            
            <div>
              <label className="text-secondary text-xs font-semibold block mb-1.5">NIN (optional)</label>
              <input value={identity.nin} onChange={e => updateI("nin", e.target.value.replace(/\D/g, "").slice(0, 11))} placeholder="11-digit NIN" className="w-full bg-surface border border-border-strong rounded-xl px-4 py-3 text-sm text-primary font-mono placeholder:text-faint focus:outline-none focus:border-brand" />
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="space-y-5">
            <div className="bg-info-light border border-info-border rounded-2xl px-4 py-3 flex items-start gap-2.5">
              <i className="ri-information-line text-info flex-shrink-0 mt-0.5" />
              <p className="text-info-text text-xs leading-relaxed">
                Upload clear photos or PDF scans. For selfie, hold your ID next to your face — both must be clearly visible.
              </p>
            </div>
            <UploadBox label={`${selectedIdType?.label || "ID"} — Front`} sublabel="Photo or PDF scan of the front" name="frontImage" file={docs.frontImage} onChange={f => setDocs(d => ({ ...d, frontImage: f }))} />
            {selectedIdType?.hasBack && (
              <UploadBox label={`${selectedIdType?.label || "ID"} — Back`} sublabel="Photo or PDF scan of the back" name="backImage" file={docs.backImage} onChange={f => setDocs(d => ({ ...d, backImage: f }))} />
            )}
            <UploadBox label="Selfie with ID" sublabel="Hold your ID next to your face — must be clearly visible" name="selfieImage" file={docs.selfieImage} onChange={f => setDocs(d => ({ ...d, selfieImage: f }))} />
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
          <div className="space-y-4">
            <div className="bg-surface rounded-2xl border border-border p-5 space-y-3">
              <p className="text-primary font-bold text-sm mb-1">Personal Information</p>
              {[
                { label: "Full Name", value: `${personal.firstName} ${personal.middleName} ${personal.lastName}`.trim() },
                { label: "DOB",       value: personal.dateOfBirth },
                { label: "Phone",     value: personal.phoneNumber },
                { label: "Address",   value: `${personal.address}, ${personal.lga}, ${personal.state}` },
              ].map(r => (
                <div key={r.label} className="flex justify-between text-sm border-b border-border pb-2 last:border-0 last:pb-0">
                  <span className="text-muted">{r.label}</span>
                  <span className="text-secondary font-medium text-right max-w-[60%] truncate">{r.value || "—"}</span>
                </div>
              ))}
            </div>
            <div className="bg-surface rounded-2xl border border-border p-5 space-y-3">
              <p className="text-primary font-bold text-sm mb-1">Identity</p>
              {[
                { label: "ID Type", value: selectedIdType?.label || "—" },
                { label: "ID No.",  value: identity.idNumber || "—" },
              ].map(r => (
                <div key={r.label} className="flex justify-between text-sm border-b border-border pb-2 last:border-0 last:pb-0">
                  <span className="text-muted">{r.label}</span>
                  <span className="text-secondary font-medium">{r.value}</span>
                </div>
              ))}
            </div>
            {/* Document thumbnails — shows PDF icon for PDFs */}
            <div className="bg-surface rounded-2xl border border-border p-5">
              <p className="text-primary font-bold text-sm mb-3">Documents</p>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: "ID Front", file: docs.frontImage },
                  { label: "ID Back",  file: docs.backImage },
                  { label: "Selfie",   file: docs.selfieImage },
                ].map(d => d.file && (
                  <div key={d.label} className="text-center">
                    {d.file.type === "application/pdf" ? (
                      <div className="w-full h-20 bg-danger-light rounded-xl border border-border flex flex-col items-center justify-center gap-1 mb-1">
                        <i className="ri-file-pdf-2-line text-danger text-xl" />
                        <p className="text-[9px] text-muted truncate px-1 w-full text-center">{d.file.name}</p>
                      </div>
                    ) : (
                      <img src={URL.createObjectURL(d.file)} alt={d.label} className="w-full h-20 object-cover rounded-xl mb-1 border border-border" />
                    )}
                    <p className="text-muted text-[10px]">{d.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-warning-light border border-warning-border rounded-2xl px-4 py-3 flex items-start gap-2.5">
              <i className="ri-shield-line text-warning flex-shrink-0 mt-0.5" />
              <p className="text-warning-text text-xs leading-relaxed">By submitting, you confirm all information and documents are genuine. False submissions may result in permanent account suspension.</p>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 mt-6">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="flex-1 py-3.5 rounded-2xl border border-border text-secondary font-bold text-sm bg-surface">
              Back
            </button>
          )}
          {step < 4 ? (
            <button onClick={() => setStep(s => s + 1)}
              disabled={step === 1 ? !step1Valid : step === 2 ? !step2Valid : !step3Valid}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-brand hover:bg-brand-hover text-on-brand">
              Continue
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex-1 py-3.5 rounded-2xl font-bold text-sm transition-all disabled:opacity-50 bg-brand hover:bg-brand-hover text-on-brand flex items-center justify-center gap-2">
              {submitting
                ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting…</>
                : <><i className="ri-send-plane-line" />Submit KYC</>}
            </button>
          )}
        </div>
      </div>
    </div>
    </ServiceGate>
    
  );
}